import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  Building2,
  FileText,
  HardDrive,
} from 'lucide-react'
import { Breadcrumb } from '../../components/Breadcrumb'
import { api, extractApiError } from '../../lib/api'
import { platformStorageResponse, plansResponse } from '../../lib/types'
import { DataTable } from '../../components/table/DataTable'
import { CountryFilter } from '../../components/ui/CountryFilter'
import { CountryCell } from '../../components/ui/CountryCell'
import { StatusBadge } from '../../components/ui/StatusBadge'
import { FilterRow } from '../../components/filters/FilterRow'
import { useChartHover } from '../../components/charts/use-chart-hover'
import { ChartTooltip } from '../../components/charts/ChartTooltip'
import { usePageSize } from '../../lib/display-prefs'
import { formatNumber } from '../../lib/format'

/** Human-readable bytes — "12.4 GB", "850 MB", "3 KB". Keeps the
 *  table narrow without losing precision at the high end. */
function fmtBytes(bytes) {
  const v = Number(bytes ?? 0)
  if (v === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(units.length - 1, Math.floor(Math.log(v) / Math.log(1024)))
  const scaled = v / Math.pow(1024, i)
  // Small magnitudes get TWO decimals (trailing zeros trimmed) so real
  // usage isn't hidden by coarse rounding — 1.33 GB displayed as
  // "1.3 GB" made the total look like it dropped ~40 MB (QA-reported).
  // At >=10 units fractions are noise, so those stay whole numbers.
  const text =
    scaled < 10 && i > 0
      ? formatNumber(scaled, { maximumFractionDigits: 2, useGrouping: false })
      : formatNumber(scaled, { maximumFractionDigits: 0, useGrouping: false })
  return `${text} ${units[i]}`
}
function fmtNumber(n) {
  return formatNumber(Number(n ?? 0))
}

/** Platform Admin → Storage. Cross-tenant rollup of File storage with
 *  per-tenant quota progress. Pairs with the plan-based storage cap
 *  the billing/limits module enforces — heaviest customers float to
 *  the top, over-quota tenants are highlighted red. */
export function PlatformStoragePage() {
  const [search, setSearch] = useState('')
  const [planFilter, setPlanFilter] = useState('all')
  // Status + country filters mirror the Companies-page vocabulary so
  // the platform-admin surfaces share one filter UX.
  const [statusFilter, setStatusFilter] = useState('all')
  const [countryFilter, setCountryFilter] = useState('all')
  const [showOverQuotaOnly, setShowOverQuotaOnly] = useState(false)
  const [page, setPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list's API accepts every standard size up to 500.
  const [limit, setLimit] = usePageSize()

  // A filter is "active" when it differs from its default. Drives the
  // Clear-all-filters button, which only appears once at least one
  // filter is narrowing the table.
  const hasAnyFilter =
    search.trim() !== '' ||
    planFilter !== 'all' ||
    statusFilter !== 'all' ||
    countryFilter !== 'all' ||
    showOverQuotaOnly

  function clearFilters() {
    setSearch('')
    setPlanFilter('all')
    setStatusFilter('all')
    setCountryFilter('all')
    setShowOverQuotaOnly(false)
    setPage(1)
  }

  // Shared server params (Search + Status + Country + Plan + over-quota
  // toggle) for both the paged query and the export "all records" fetch.
  function filterParams() {
    const params = {}
    if (search.trim()) params.search = search.trim()
    if (planFilter !== 'all') params.plan = planFilter
    if (statusFilter !== 'all') params.status = statusFilter
    if (countryFilter !== 'all') params.country = countryFilter
    if (showOverQuotaOnly) params.overQuota = 1
    return params
  }

  const storageQuery = useQuery({
    queryKey: [
      'platform-storage',
      search,
      planFilter,
      statusFilter,
      countryFilter,
      showOverQuotaOnly,
      page,
      limit,
    ],
    queryFn: async () => {
      const res = await api.get('/super-admin/platform/storage', {
        params: { ...filterParams(), page, limit },
      })
      return platformStorageResponse.parse(res.data)
    },
    placeholderData: (prev) => prev,
  })

  // Plan catalogue — used for the filter dropdown and the row pill so
  // custom plans surface alongside the seeded ones.
  const plansQuery = useQuery({
    queryKey: ['platform-plans-dropdown'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get('/super-admin/plans')
      return plansResponse.parse(res.data)
    },
  })
  const planOptions = useMemo(() => {
    const list = plansQuery.data?.data ?? []
    return [...list]
      // Hide inactive ("Hidden"/draft) plans from the filter.
      .filter((p) => p.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      .map((p) => ({ value: p.code, label: p.name }))
  }, [plansQuery.data])
  const planNameByCode = useMemo(() => {
    const map = new Map()
    for (const p of plansQuery.data?.data ?? []) map.set(p.code, p.name)
    return map
  }, [plansQuery.data])

  const rows = storageQuery.data?.data ?? []
  const totals = storageQuery.data?.totals
  const pageInfo = storageQuery.data?.pagination ?? null

  // Export pages through the filtered feed (bounded); DataTable sorts the
  // current page internally via each column's `sortAccessor`.
  async function fetchAllForExport() {
    const all = []
    for (let p = 1; p <= 50; p += 1) {
      const res = await api.get('/super-admin/platform/storage', {
        params: { ...filterParams(), page: p, limit: 500 },
      })
      const parsed = platformStorageResponse.parse(res.data)
      all.push(...(parsed.data ?? []))
      const info = parsed.pagination
      if (!info || p >= info.totalPages) break
    }
    return all
  }

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Company',
        grow: true,
        pinnable: true,
        minWidth: 220,
        sortAccessor: (r) => (r.name ?? '').toLowerCase(),
        exportValue: (r) => r.name ?? '',
        render: (r) => (
          <div className="flex items-center gap-2 min-w-0">
            <p className="font-semibold text-[color:var(--color-ink-strong)] truncate">
              {r.name}
            </p>
            {r.overQuota ? (
              <span
                className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded shrink-0 bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger)] border border-[color:var(--color-danger-border)]"
                title="Storage usage is at or above the plan quota."
              >
                <AlertTriangle size={10} />
                Over quota
              </span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        minWidth: 110,
        sortAccessor: (r) => r.status ?? '',
        exportValue: (r) => r.status ?? '',
        render: (r) => <StatusBadge status={r.status} />,
      },
      {
        key: 'plan',
        header: 'Plan',
        minWidth: 130,
        sortAccessor: (r) =>
          (planNameByCode.get(r.planTier) ?? r.planTier ?? '').toLowerCase(),
        exportValue: (r) =>
          r.planTier?.trim() ? (planNameByCode.get(r.planTier) ?? r.planTier) : 'Awaiting plan',
        render: (r) => (
          <PlanPill planTier={r.planTier} planNameByCode={planNameByCode} />
        ),
      },
      {
        key: 'country',
        header: 'Country',
        minWidth: 120,
        sortAccessor: (r) => (r.country ?? '').toLowerCase(),
        exportValue: (r) => r.country ?? '',
        render: (r) => (
          <CountryCell country={r.country} billingCurrency={r.billingCurrency} />
        ),
      },
      {
        key: 'fileCount',
        header: 'Files',
        minWidth: 90,
        sortAccessor: (r) => Number(r.fileCount ?? 0),
        exportValue: (r) => Number(r.fileCount ?? 0),
        cellClassName: 'text-[color:var(--color-ink-strong)] tabular-nums',
        render: (r) => fmtNumber(r.fileCount),
      },
      {
        key: 'usedBytes',
        header: 'Used',
        minWidth: 100,
        sortAccessor: (r) => Number(r.usedBytes ?? 0),
        exportValue: (r) => fmtBytes(r.usedBytes),
        cellClassName: 'text-[color:var(--color-ink-strong)] tabular-nums',
        render: (r) => fmtBytes(r.usedBytes),
      },
      {
        key: 'quotaBytes',
        header: 'Quota',
        minWidth: 100,
        sortAccessor: (r) =>
          r.quotaBytes == null ? Number.NEGATIVE_INFINITY : Number(r.quotaBytes),
        exportValue: (r) =>
          r.quotaBytes === null
            ? r.planTier?.trim()
              ? ''
              : 'No plan'
            : fmtBytes(r.quotaBytes),
        cellClassName: 'text-[color:var(--color-ink-muted)] tabular-nums',
        render: (r) =>
          r.quotaBytes === null
            ? r.planTier?.trim()
              ? '—'
              : 'No plan'
            : fmtBytes(r.quotaBytes),
      },
      {
        key: 'pctUsed',
        header: 'Usage',
        minWidth: 160,
        sortAccessor: (r) =>
          r.pctUsed == null ? Number.NEGATIVE_INFINITY : Number(r.pctUsed),
        exportValue: (r) => (r.pctUsed == null ? '' : `${r.pctUsed}%`),
        render: (r) => (
          <QuotaBar pct={r.pctUsed} quota={r.quotaBytes} planTier={r.planTier} />
        ),
      },
    ],
    [planNameByCode],
  )

  return (
    <div>
      <Breadcrumb current="Storage" homePath="/super-admin/dashboard" />

      <div className="mb-5">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <HardDrive
            size={20}
            className="text-[color:var(--color-accent-strong)]"
          />
          Storage
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          See how much storage each company is using.
        </p>
      </div>

      {/* Totals strip — same `KpiCard` chrome as Companies / Plans
       *  / All Users / AI Usage pages so all five platform-admin
       *  list surfaces share one KPI vocabulary. The "Over Quota"
       *  card is a pure risk signal: it's only rendered when at least
       *  one tenant has actually breached their cap (rose tone). In the
       *  healthy state it would just read "0", so it's hidden and the
       *  strip reflows to three evenly-spaced cards. */}
      {totals ? (
        <div
          className={
            'grid grid-cols-1 md:grid-cols-2 gap-4 mb-5 ' +
            (totals.tenantsOverQuota > 0 ? 'lg:grid-cols-4' : 'lg:grid-cols-3')
          }
        >
          <KpiCard
            label="Total Companies"
            value={fmtNumber(totals.tenantCount)}
            icon={Building2}
            tone="violet"
          />
          <KpiCard
            label="Total Storage Used"
            value={fmtBytes(totals.usedBytes)}
            icon={HardDrive}
            tone="sky"
          />
          <KpiCard
            label="Total Files"
            value={fmtNumber(totals.fileCount)}
            icon={FileText}
            tone="emerald"
          />
          {totals.tenantsOverQuota > 0 ? (
            <KpiCard
              label="Over Quota"
              value={fmtNumber(totals.tenantsOverQuota)}
              icon={AlertTriangle}
              tone="rose"
            />
          ) : null}
        </div>
      ) : null}

      {/* Filter bar — shared FilterRow primitive. CountryFilter keeps
       *  its custom popover. Over-quota toggle stays as a sibling
       *  checkbox. */}
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={(v) => {
            setSearch(v)
            setPage(1)
          }}
          placeholder="Search by Company Name"
        />
        <FilterRow.Select
          ariaLabel="Status"
          placeholder="All statuses"
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All statuses' },
            { value: 'active', label: 'Active' },
            { value: 'suspended', label: 'Suspended' },
            { value: 'offboarded', label: 'Offboarded' },
          ]}
        />
        <CountryFilter
          value={countryFilter}
          onChange={(next) => {
            setCountryFilter(next)
            setPage(1)
          }}
        />
        <FilterRow.Select
          ariaLabel="Plan"
          placeholder="All plans"
          value={planFilter}
          onChange={(v) => {
            setPlanFilter(v)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All plans' },
            ...planOptions,
          ]}
        />
        <label className="inline-flex items-center gap-2 text-sm text-[color:var(--color-ink)] cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showOverQuotaOnly}
            onChange={(e) => {
              setShowOverQuotaOnly(e.target.checked)
              setPage(1)
            }}
            className="accent-[color:var(--color-priority-urgent)] cursor-pointer"
          />
          Over quota only
        </label>
        <FilterRow.Spacer />
        <FilterRow.Clear show={hasAnyFilter} onClick={clearFilters} />
        {/* Manual refresh — fires the same query React Query would fire
         *  on window-focus, but on demand. The spinner state animates
         *  the icon while the request is in flight so the user gets
         *  feedback that the click landed (bug 0013274: button rendered
         *  with no onClick and looked dead). */}
        <FilterRow.Refresh
          onClick={() => storageQuery.refetch()}
          loading={storageQuery.isFetching}
        />
      </FilterRow>

      {/* Server-paginated per-tenant storage on the shared DataTable.
       *  Search + Status + Country + Plan + "over quota only" filters and
       *  the KPI strip above stay page-owned. Read-only — no row actions;
       *  `getRowProps` re-adds the danger-tinted highlight for over-quota
       *  rows the old table drew inline. */}
      <DataTable
        tableId="platform-storage"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        getRowProps={(r) =>
          r.overQuota ? { className: 'bg-[color:var(--color-danger-bg)]' } : {}
        }
        loading={storageQuery.isLoading}
        fetching={storageQuery.isFetching}
        error={
          storageQuery.isError
            ? extractApiError(storageQuery.error).message
            : null
        }
        onRetry={() => storageQuery.refetch()}
        searchable={false}
        defaultSort={[{ key: 'usedBytes', dir: 'desc' }]}
        page={pageInfo?.page ?? page}
        totalPages={pageInfo?.totalPages ?? 1}
        total={pageInfo?.total ?? 0}
        limit={pageInfo?.limit ?? limit}
        onPageChange={setPage}
        onLimitChange={(n) => {
          setLimit(n)
          setPage(1)
        }}
        exportBaseName="storage"
        exportTitle="Storage"
        getExportRows={fetchAllForExport}
        emptyState={{
          icon: HardDrive,
          title: 'No records found',
        }}
      />
    </div>
  )
}

/** Per-tone tile + halo bleed for the KPI strip. Same palette as
 *  Companies / Plans / All Users / AI Usage so all five platform-
 *  admin surfaces share one visual vocabulary. */
const KPI_TONES = {
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-white/[0.06] dark:text-violet-300 dark:border-white/10',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
  },
  amber: {
    tile: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-white/[0.06] dark:text-orange-300 dark:border-white/10',
    halo: 'from-[#FFEDD5] to-transparent dark:from-transparent',
  },
  sky: {
    tile: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
    halo: 'from-[#DBEAFE] to-transparent dark:from-transparent',
  },
  rose: {
    tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
  },
}

/** Single-stat KPI card matching the Companies-page `KpiCard` shape
 *  beat-for-beat — rounded-xl chrome, blurred halo bleed top-right,
 *  12×12 tinted icon chip, 4xl font-display value, uppercase
 *  tracking-wider label. Accepts both numeric and pre-formatted
 *  string values (the Storage page renders "127 KB" via fmtBytes
 *  before passing the value in). */
function KpiCard({ label, value, icon: Icon, tone = 'violet', hint }) {
  const tones = KPI_TONES[tone] ?? KPI_TONES.violet
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 min-h-[120px]">
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0">
            {label}
          </h3>
          <p className="mt-2 font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
            {typeof value === 'number'
              ? formatNumber(value)
              : value}
          </p>
          {hint ? (
            <p className="mt-2 text-[13px] text-[color:var(--color-ink-muted)]">
              {hint}
            </p>
          ) : null}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

function PlanPill({ planTier, planNameByCode }) {
  // A no-plan company (planTier '' or null — the app-wide "No plan" sentinel)
  // shows the canonical amber "Awaiting plan" badge, matching the Companies page
  // (TenantsPage), instead of a blank accent pill.
  const code = typeof planTier === 'string' ? planTier.trim() : planTier
  if (!code) {
    return (
      <span
        className="inline-flex text-xs uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]"
        title="Awaiting plan selection — this self-signup company hasn't chosen a plan yet and is routed to Billing to pick one."
      >
        Awaiting plan
      </span>
    )
  }
  return (
    <span className="inline-flex text-xs uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]">
      {planNameByCode?.get(code) ?? code}
    </span>
  )
}

/** Inline storage usage bar. Quota=null on the server comes from TWO
 *  different states: (a) an enterprise / unlimited plan (legitimately
 *  no cap) and (b) a tenant with no plan assigned (nothing to derive a
 *  cap from). We split them by inspecting `planTier` — an empty string
 *  is the app-wide "No plan" sentinel (types.js). Rendering "Unlimited"
 *  for a no-plan tenant read as "granted infinite storage" (QA bug
 *  0016514); "No plan" makes it clear the row is uncapped because it
 *  is unbilled, not because it is entitled. */
function QuotaBar({ pct, quota, planTier }) {
  const { ref, hover, track, clear } = useChartHover()
  const hasPlan = typeof planTier === 'string' && planTier.trim() !== ''
  if (quota === null || quota === undefined) {
    if (!hasPlan) {
      return (
        <span className="text-sm text-[color:var(--color-ink-muted)] italic">
          No plan
        </span>
      )
    }
    return (
      <span className="text-sm text-[color:var(--color-ink-muted)] italic">
        Unlimited
      </span>
    )
  }
  const value = pct ?? 0
  const tone =
    value >= 100
      ? 'bg-[color:var(--color-priority-urgent)]'
      : value >= 80
        ? 'bg-[color:var(--color-warning)]'
        : 'bg-[color:var(--color-primary)]'
  // Colour dot for the tooltip mirrors the bar's green/amber/red tone.
  const dotColor =
    value >= 100
      ? 'var(--color-priority-urgent)'
      : value >= 80
        ? 'var(--color-warning)'
        : 'var(--color-primary)'
  const payload = {
    color: dotColor,
    label: 'Storage',
    value: `${Math.round(value)}% used`,
    hint: `Quota ${fmtBytes(quota)}`,
  }
  return (
    <div className="relative flex items-center gap-2">
      {/* Track uses `--color-rule` instead of `--color-canvas` —
       *  the canvas tone is identical to the table's row-hover
       *  background, so on hover the empty track blended into the
       *  row and the whole bar visually disappeared (especially at
       *  0% fill). `--color-rule` is the standard border tone and
       *  stays visible against every row state. */}
      <div
        ref={ref}
        className="flex-1 h-1.5 rounded-full bg-[color:var(--color-rule)] overflow-hidden"
        onMouseEnter={track(payload)}
        onMouseMove={track(payload)}
        onMouseLeave={clear}
      >
        <div
          className={'h-full rounded-full transition-[width] duration-500 ' + tone}
          // Render empty (0%) when the company has zero storage used,
          // otherwise keep the 2% floor so a tiny-but-non-zero value
          // is still visible.
          style={{ width: `${value > 0 ? Math.min(100, Math.max(2, value)) : 0}%` }}
        />
      </div>
      <span className="text-xs text-[color:var(--color-ink-muted)] tabular-nums shrink-0 w-12 text-right">
        {value}%
      </span>
      {hover ? <ChartTooltip {...hover} /> : null}
    </div>
  )
}

